import React, { useEffect, useReducer, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { useTheme } from "styled-components";
import { FaSteam } from "react-icons/fa";
import { HiClock } from "react-icons/hi";

import { ClipQualityIcon } from "MVVM/Components/Icons";
import { SteambotEnroll } from "MVVM/Components/SteambotEnroll";
import { useActiveClips } from "MVVM/Components/SiteNav/UserMenu/useActiveClips";
import { useClipQualityConfigs } from "MVVM/Hooks/useClipQualityConfigs";
import {
  useClipQualitySettingsModal,
  useCs2HudSettingsModal,
} from "MVVM/Hooks/useClipQualitySettingsModal";
import { useCs2Hud } from "MVVM/Hooks/useCs2Hud";
import { useMediaQuery } from "MVVM/Hooks/useMediaQuery";
import { usePortal } from "MVVM/Hooks/usePortal";
import { useSteambot } from "MVVM/Hooks/useSteambot";
import { useUnsavedDraftCount } from "MVVM/Hooks/useUnsavedDraftCount";
import { InFlightClipsAtom } from "MVVM/State/clipStatus";
import { analytics } from "MVVM/Analytics";
import cs2Logo from "MVVM/Assets/GameLogos/cs2.svg";
import type { TDockCardProps } from "./DockCard";
import { SteambotModalBody, SteambotModalTitle } from "./styledComponents";

export type TNudge = Omit<TDockCardProps, "onDismiss"> & {
  id: string;
  kind: "risk" | "promo";
};

type TDismissals = Record<string, { until: number; count: number }>;

const STORAGE_KEY = "dock-dismissals";
const SESSION_PROMO_KEY = "dock-session-promo";
const LEGACY_KEYS: Record<string, string> = {
  steambot: "steambot-bubble-dismissed",
  cs2Hud: "cs2-hud-bubble-dismissed",
};
// Existing "Promotion - *" analytics names, kept so dashboards don't break.
const PROMO_NAMES: Record<string, string> = {
  steambot: "steambotBubble",
  cs2Hud: "cs2HudBubble",
};
const DAY_MS = 86_400_000;
const FOREVER = Number.MAX_SAFE_INTEGER;
const READY_QUIET_MS = 60_000;
const QUIET_PROMO_PATHS = ["/clip/", "/advanced-creation/"];

export const MAX_VISIBLE = 2;

// Candidates are in priority order. `visible` pops up on its own (one promo per
// session at most); `available` is everything the "more" toggle can reveal.
export const pickNudges = (
  candidates: (TNudge | false)[],
  dismissals: TDismissals,
  promosQuiet: boolean,
  sessionPromo: string | null,
  now = Date.now(),
) => {
  const available = candidates.filter(
    (c): c is TNudge =>
      !!c &&
      (dismissals[c.id]?.until ?? 0) < now &&
      (c.kind === "risk" || !promosQuiet),
  );
  let promoPicked = false;
  const visible = available
    .filter((c) => {
      if (c.kind === "risk") return true;
      if (promoPicked || (sessionPromo && sessionPromo !== c.id)) return false;
      promoPicked = true;
      return true;
    })
    .slice(0, MAX_VISIBLE);
  return { available, visible };
};

export const readDismissals = (): TDismissals => {
  try {
    const stored: TDismissals = JSON.parse(
      localStorage.getItem(STORAGE_KEY) ?? "{}",
    );
    Object.entries(LEGACY_KEYS).forEach(([id, key]) => {
      if (!localStorage.getItem(key)) return;
      stored[id] = { until: FOREVER, count: 2 };
      localStorage.removeItem(key);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    });
    return stored;
  } catch {
    return {};
  }
};

// Risk nudges snooze 3 days. Promos snooze 14 days, then stay gone.
export const snooze = (current: TDismissals, nudge: TNudge): TDismissals => {
  const count = (current[nudge.id]?.count ?? 0) + 1;
  const until =
    nudge.kind === "risk"
      ? Date.now() + 3 * DAY_MS
      : count >= 2
      ? FOREVER
      : Date.now() + 14 * DAY_MS;
  const next = { ...current, [nudge.id]: { until, count } };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // storage blocked: the dismissal still holds for this session
  }
  return next;
};

const readSessionPromo = () => {
  try {
    return sessionStorage.getItem(SESSION_PROMO_KEY);
  } catch {
    return null;
  }
};

const useRecentlyReady = () => {
  const inFlight = useRecoilValue(InFlightClipsAtom);
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const lastReadyAt = Math.max(
    0,
    ...inFlight.map((t) => Date.parse(t.desktop?.completedAt ?? "") || 0),
  );

  useEffect(() => {
    const remaining = lastReadyAt + READY_QUIET_MS - Date.now();
    if (remaining <= 0) return undefined;
    const timer = setTimeout(rerender, remaining);
    return () => clearTimeout(timer);
  }, [lastReadyAt]);

  return lastReadyAt + READY_QUIET_MS > Date.now();
};

export const useDockNudges = () => {
  const history = useHistory();
  const { pathname } = useLocation();
  const { breaks } = useTheme() as { breaks: Record<string, string> };
  const isPhone = useMediaQuery(`(max-width: ${breaks.small})`);
  const jobInFlight = useActiveClips().length > 0;
  const recentlyReady = useRecentlyReady();

  const unsavedCount = useUnsavedDraftCount();
  const { currentConfig, needsConfChange } = useClipQualityConfigs();
  const { open: openClipQuality } = useClipQualitySettingsModal();
  const { showBubble: steambotEligible } = useSteambot();
  const cs2Hud = useCs2Hud();
  const { open: openCs2Hud } = useCs2HudSettingsModal();

  const [dismissals, setDismissals] = useState(readDismissals);
  const [sessionPromo, setSessionPromo] = useState(readSessionPromo);

  // Portalled to #root so the modal stays mounted after enrolling hides the nudge.
  const steambotModal = usePortal(
    <SteambotModalBody>
      <SteambotModalTitle>Add the Allstar Steam Bot</SteambotModalTitle>
      <SteambotEnroll />
    </SteambotModalBody>,
  );

  const onLibrary = pathname === "/library";
  const candidates: (TNudge | false)[] = [
    !!unsavedCount &&
      !onLibrary && {
        id: "unsavedDrafts",
        kind: "risk",
        icon: <HiClock size={18} />,
        tone: "caution",
        title: `${unsavedCount} unsaved draft${
          unsavedCount === 1 ? "" : "s"
        } expiring soon`,
        meta: "Draft clips expire 72h after creation",
        actionLabel: "View",
        onAction: () => history.push("/library?saved=unsaved"),
      },
    needsConfChange &&
      !onLibrary && {
        id: "clipQuality",
        kind: "risk",
        icon: <ClipQualityIcon size={18} />,
        tone: currentConfig ? "caution" : "neutral",
        title: currentConfig
          ? "Update your clip quality settings"
          : "Set your clip quality settings",
        meta: currentConfig
          ? "Your current settings need an update"
          : "Pick your resolution and aspect ratio",
        actionLabel: currentConfig ? "Update" : "Set up",
        onAction: () => openClipQuality(),
      },
    steambotEligible && {
      id: "steambot",
      kind: "promo",
      icon: <FaSteam size={18} />,
      title: "Add the Allstar Steam Bot",
      meta: "Instant Autocapture clips",
      actionLabel: "Add bot",
      onAction: () => steambotModal.setPortalOpen(true),
    },
    cs2Hud.isLoggedIn &&
      !cs2Hud.isConfigured && {
        id: "cs2Hud",
        kind: "promo",
        icon: <img alt="" src={cs2Logo} />,
        title: "Set up your CS2 HUD & Crosshair",
        meta: "Use your own crosshair and HUD in clips",
        actionLabel: "Set up",
        onAction: () => openCs2Hud(),
      },
  ];

  const promosQuiet =
    jobInFlight ||
    recentlyReady ||
    isPhone ||
    QUIET_PROMO_PATHS.some((path) => pathname.startsWith(path));

  const { available, visible } = pickNudges(
    candidates,
    dismissals,
    promosQuiet,
    sessionPromo,
  );
  const hiddenCount = available.length - visible.length;
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? available : visible;

  useEffect(() => {
    if (hiddenCount === 0) setExpanded(false);
  }, [hiddenCount]);

  const track = (event: string, nudge: TNudge) =>
    nudge.kind === "promo" &&
    analytics.track(event, {
      type: "bubble",
      promotionName: PROMO_NAMES[nudge.id],
    });

  const autoPromoId = visible.find((n) => n.kind === "promo")?.id;
  useEffect(() => {
    if (!autoPromoId || sessionPromo) return;
    try {
      sessionStorage.setItem(SESSION_PROMO_KEY, autoPromoId);
    } catch {
      // storage blocked: the cap still holds while this tab lives
    }
    setSessionPromo(autoPromoId);
  }, [autoPromoId, sessionPromo]);

  const impressions = useRef(new Set<string>());
  const shownIds = shown.map((n) => n.id).join();
  useEffect(() => {
    shown.forEach((nudge) => {
      if (impressions.current.has(nudge.id)) return;
      impressions.current.add(nudge.id);
      track("Promotion - Impression", nudge);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownIds]);

  return {
    nudges: shown.map((nudge) => ({
      ...nudge,
      onAction: () => {
        track("Promotion - Button Click", nudge);
        nudge.onAction();
      },
      onDismiss: () => {
        track("Promotion - Dismiss", nudge);
        setDismissals((current) => snooze(current, nudge));
      },
    })),
    hiddenCount,
    expanded,
    toggleExpanded: () => setExpanded((open) => !open),
    modals: steambotModal.portalOpen && steambotModal.Portal,
  };
};
